import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EVENTS_FOR_TARGET, EVENT_LABELS, EVENT_HELP, DEFAULT_DEFAULTS } from "@/lib/notifications";
import { savedNearZip } from "@/lib/filterCookies";
import { useMyLoc, geoParams } from "@/lib/geoLocation";
import { BookmarkPlus, BookmarkCheck, Bell, Users, User, Dumbbell, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
    SectionHeader, ListControls, usePersistedCtrl, sortItems, Pager, ThumbCol, ListingBody, thumbFor,
} from "@/components/listingUI";
import { eventEndsAt } from "@/lib/calendar";
import { todayLocalISO } from "@/lib/formatDate";
import { useShowExpired, ExpiredEventsToggle } from "@/components/ExpiredToggle";

// An event is "past/expired" using the same rule as the listing card's "Event passed" label.
function isEventPast(e) {
    const ee = eventEndsAt(e);
    return ee ? Date.now() >= ee.getTime() : (e.end_date || e.event_date || "") < todayLocalISO();
}

// Section config — title/colour/icon + sort fields & defaults identical to the Landing results list.
const SECTIONS = [
    { dataKey: "teams", targetType: "team", title: "Teams", color: "#ff4500", icon: Users,
      fields: [["rating", "Rating"], ["name", "Name"]], defaults: { sort: "name", dir: "asc", pageSize: 5 } },
    { dataKey: "trainers", targetType: "trainer", title: "Trainers", color: "#2196f3", icon: Dumbbell,
      fields: [["rating", "Rating"], ["name", "Name"]], defaults: { sort: "name", dir: "asc", pageSize: 5 } },
    { dataKey: "trainer_listings", targetType: "trainer_listing", title: "Trainer Listings", color: "#2196f3", icon: Dumbbell,
      fields: [["rating", "Rating"], ["name", "Name"]], defaults: { sort: "name", dir: "asc", pageSize: 5 } },
    { dataKey: "events", targetType: "event", title: "Events", color: "#22c55e", icon: Calendar,
      fields: [["date", "Date"], ["name", "Name"]], defaults: { sort: "date", dir: "asc", pageSize: 5 } },
    { dataKey: "users", targetType: "user", title: "People", color: "#a855f7", icon: User,
      fields: [["name", "Name"]], defaults: { sort: "name", dir: "asc", pageSize: 5 } },
];

// Bell icon → popover of selectable alert-preference checkboxes.
function AlertsBell({ targetType, itemId, initialPrefs, defaults }) {
    const { setFollowPrefs } = useAuth();
    const ACCENT = { team: "#ff4500", trainer: "#2196f3", trainer_listing: "#2196f3", event: "#22c55e", user: "#a855f7" };
    const accent = ACCENT[targetType] || "#ff4500";
    const [prefs, setPrefs] = useState(initialPrefs || {});
    const [saving, setSaving] = useState(false);
    const events = EVENTS_FOR_TARGET[targetType] || [];
    const effective = (t) => (t in prefs ? !!prefs[t] : !!defaults[t]);

    const toggle = async (t) => {
        const next = { ...prefs, [t]: !effective(t) };
        setPrefs(next);
        setSaving(true);
        const res = await setFollowPrefs(targetType, itemId, next);
        setSaving(false);
        if (!res.ok) toast.error(res.error || "Could not save alert setting");
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button type="button" title="Alert preferences" data-testid={`following-alerts-toggle-${itemId}`}
                    className="inline-flex items-center justify-center h-8 w-8 text-[#a0a0a0] hover:text-[#ff4500] transition-colors shrink-0">
                    <Bell className="h-5 w-5" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 bg-[#0f0f0f] border-[#2a2a2a] rounded-none p-3" data-testid={`following-alerts-panel-${itemId}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Alert me when {saving ? "· saving…" : ""}</div>
                <div className="space-y-2">
                    {events.map((t) => (
                        <label key={t} className="flex items-start gap-3 cursor-pointer select-none py-1" data-testid={`pref-${itemId}-${t}`}>
                            <input type="checkbox" checked={effective(t)} onChange={() => toggle(t)} style={{ accentColor: accent }} className="mt-0.5 h-4 w-4" />
                            <span className="text-sm text-white leading-tight">
                                {EVENT_LABELS[t]}
                                <span className="block text-[11px] text-[#a0a0a0]">{EVENT_HELP[t]}</span>
                            </span>
                        </label>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

// Broken-heart (following) → tap to unfollow; flips to a solid heart to re-follow.
function FollowHeart({ targetType, itemId, following, onToggle }) {
    return (
        <button type="button" onClick={onToggle} title={following ? "Unfollow" : "Follow"} data-testid={`unfollow-${itemId}`}
            className="inline-flex items-center justify-center h-8 w-8 transition-colors shrink-0 text-[#a0a0a0] hover:text-white">
            {following ? <BookmarkCheck className="h-5 w-5" /> : <BookmarkPlus className="h-5 w-5" />}
        </button>
    );
}

function FollowRow({ item, targetType, color, defaults }) {
    const { toggleFollow } = useAuth();
    const [following, setFollowing] = useState(true);
    const { src, fallback, color: thumbColor } = thumbFor(item, targetType);

    const onToggle = async () => {
        const res = await toggleFollow(targetType, item.id);
        if (res.ok) {
            setFollowing(res.following);
            toast.success(res.following ? "Following again" : "Unfollowed");
        } else {
            toast.error(res.error || "Could not update");
        }
    };

    return (
        <div className="flex items-center gap-3 border border-[#2a2a2a] bg-[#141414] px-3 py-1.5" style={{ borderLeft: `3px solid ${color}` }} data-testid={`following-${targetType}-${item.id}`}>
            <Link to={item.link || "/"} className={`group flex items-center gap-3 min-w-0 flex-1 transition-opacity ${following ? "" : "opacity-50"}`}>
                <ThumbCol src={src} fallback={fallback} color={thumbColor} distance={item.distance_miles} {...(targetType === "event" ? { bg: "black", fit: "contain" } : {})} />
                <ListingBody item={item} type={targetType} />
            </Link>
            <div className="flex flex-col sm:flex-row items-center gap-0.5 sm:gap-1.5 shrink-0">
                <AlertsBell targetType={targetType} itemId={item.id} initialPrefs={item.prefs} defaults={defaults} />
                <FollowHeart targetType={targetType} itemId={item.id} following={following} onToggle={onToggle} />
            </div>
        </div>
    );
}

// One section: header (icon + colour) + sort controls + rows + pager (identical to Landing).
function FollowSection({ cfg, items, defaults }) {
    const [ctrl, setCtrl] = usePersistedCtrl(`fav-${cfg.dataKey}`, cfg.defaults);
    const [showExpired, setShowExpired] = useShowExpired();
    if (!items || items.length === 0) return null;
    const isEvents = cfg.targetType === "event";
    const list = isEvents && !showExpired ? items.filter((e) => !isEventPast(e)) : items;
    const sorted = sortItems(list, ctrl.sort, ctrl.dir);
    const paged = sorted.slice(ctrl.page * ctrl.pageSize, ctrl.page * ctrl.pageSize + ctrl.pageSize);
    return (
        <div>
            <SectionHeader label={cfg.title} color={cfg.color} icon={cfg.icon}>
                <ListControls testid={`fav-${cfg.dataKey}-controls`} ctrl={ctrl} setCtrl={setCtrl} fields={cfg.fields} className="flex flex-wrap items-center gap-2 ml-auto">
                    {isEvents && <ExpiredEventsToggle showExpired={showExpired} onToggle={() => setShowExpired((v) => !v)} testid="fav-events-toggle-expired" />}
                </ListControls>
            </SectionHeader>
            <div className="grid grid-cols-1 gap-3" data-testid={`fav-${cfg.dataKey}-grid`}>
                {paged.map((it) => (
                    <FollowRow key={`${cfg.targetType}-${it.id}`} item={it} targetType={cfg.targetType} color={cfg.color} defaults={defaults} />
                ))}
            </div>
            <Pager testid={`fav-${cfg.dataKey}-pager`} ctrl={ctrl} setCtrl={setCtrl} total={list.length} />
        </div>
    );
}

export default function Favorites() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ teams: [], trainers: [], users: [], events: [], trainer_listings: [] });
    const [defaults, setDefaults] = useState(DEFAULT_DEFAULTS);
    const [loading, setLoading] = useState(true);
    // Top filter — which groups are shown. Events selected by default.
    const [selected, setSelected] = useState(() => new Set(["events"]));
    const [myLoc] = useMyLoc();

    const load = useCallback(async () => {
        try {
            const params = geoParams(myLoc, savedNearZip());
            const [following, settings] = await Promise.all([
                api.get("/following", { params }),
                api.get("/users/me/notification-settings"),
            ]);
            setData(following.data);
            setDefaults(settings.data.defaults || DEFAULT_DEFAULTS);
        } catch { /* ignore */ } finally {
            setLoading(false);
        }
    }, [myLoc]);

    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate("/login"); return; }
        load();
    }, [user, authLoading, navigate, load]);

    // Two-level sticky offsets: the "graphic" filter row pins under the site header,
    // section headers pin under that row.
    useEffect(() => {
        const header = document.querySelector('[data-testid="site-header"]');
        const bar = document.querySelector('[data-testid="following-filters"]');
        const measure = () => {
            const h = header ? header.offsetHeight : 64;
            document.documentElement.style.setProperty("--tsi-sticky-top", `${h}px`);
            document.documentElement.style.setProperty("--tsi-follows-top", `${h + (bar ? bar.offsetHeight : 0)}px`);
        };
        measure();
        const ro = new ResizeObserver(measure);
        if (header) ro.observe(header);
        if (bar) ro.observe(bar);
        window.addEventListener("resize", measure);
        return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
    }, [data, selected, loading]);

    if (authLoading || loading) {
        return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;
    }

    const total = SECTIONS.reduce((n, s) => n + (data[s.dataKey]?.length || 0), 0);

    // Filter groups map each button to the underlying section dataKeys + a combined count.
    const GROUPS = [
        { key: "teams", label: "Teams", color: "#ff4500", icon: Users, dataKeys: ["teams"] },
        { key: "trainers", label: "Trainers", color: "#2196f3", icon: Dumbbell, dataKeys: ["trainers", "trainer_listings"] },
        { key: "events", label: "Events", color: "#22c55e", icon: Calendar, dataKeys: ["events"] },
        { key: "people", label: "People", color: "#a855f7", icon: User, dataKeys: ["users"] },
    ];
    const groupCount = (g) => g.dataKeys.reduce((n, dk) => n + (data[dk]?.length || 0), 0);

    const toggle = (key) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // Only render sections whose group is currently selected.
    const allowedKeys = new Set(GROUPS.filter((g) => selected.has(g.key)).flatMap((g) => g.dataKeys));
    const visibleSections = SECTIONS.filter((s) => allowedKeys.has(s.dataKey));
    const anyVisibleItems = visibleSections.some((s) => (data[s.dataKey]?.length || 0) > 0);

    return (
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-10">
            <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-2" data-testid="following-heading">Your follows</div>
            <div className="h-px bg-[#2a2a2a] my-6" />

            {total === 0 ? (
                <div className="border border-[#2a2a2a] py-20 text-center" data-testid="following-empty">
                    <BookmarkPlus className="h-8 w-8 text-[#ff4500] mx-auto mb-3" />
                    <div className="font-display text-3xl">You're not following anyone yet</div>
                    <p className="text-sm text-[#a0a0a0] mt-2">Follow teams, trainers, and events to get updates.</p>
                    <Link to="/" className="mt-6 inline-block bg-[#ff4500] hover:bg-[#ff5722] text-white px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold">
                        Browse Teams
                    </Link>
                </div>
            ) : (
                <>
                    <div className="sticky z-40 -mx-6 md:-mx-8 px-6 md:px-8 py-2 mb-8 bg-[#0a0a0a] flex flex-wrap gap-2" data-testid="following-filters" style={{ top: "var(--tsi-sticky-top, 64px)" }}>
                        {GROUPS.map((g) => {
                            const on = selected.has(g.key);
                            const Icon = g.icon;
                            return (
                                <button
                                    key={g.key}
                                    type="button"
                                    onClick={() => toggle(g.key)}
                                    data-testid={`following-filter-${g.key}`}
                                    aria-pressed={on}
                                    title={g.label}
                                    className={`inline-flex items-center gap-2 h-10 px-3 md:px-4 border text-sm font-semibold uppercase tracking-[0.15em] transition-colors ${on ? "bg-[#141414]" : "bg-transparent border-[#2a2a2a] text-[#a0a0a0] hover:text-white hover:border-white/30"}`}
                                    style={on ? { borderColor: g.color, color: g.color, backgroundColor: `${g.color}1a` } : undefined}
                                >
                                    <Icon className="h-4 w-4 shrink-0" />
                                    <span data-testid={`following-filter-${g.key}-count`}>{groupCount(g)}</span>
                                    <span className="hidden md:inline">{g.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {visibleSections.length === 0 ? (
                        <div className="border border-[#2a2a2a] py-14 text-center text-sm text-[#a0a0a0]" data-testid="following-none-selected">
                            Select a category above to see who you're following.
                        </div>
                    ) : !anyVisibleItems ? (
                        <div className="border border-[#2a2a2a] py-14 text-center text-sm text-[#a0a0a0]" data-testid="following-group-empty">
                            You're not following anything in the selected {visibleSections.length > 1 ? "categories" : "category"} yet.
                        </div>
                    ) : (
                        <div className="space-y-10">
                            {visibleSections.map((cfg) => (
                                <FollowSection key={cfg.dataKey} cfg={cfg} items={data[cfg.dataKey] || []} defaults={defaults} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
